import React from "react";
import { ArrowRight, Hash, Search } from "lucide-react";
import SearchableSelect from "modules/swiftboard/components/ui/SearchableSelect";
import DateTimePicker from "modules/swiftboard/components/ui/DateTimePicker";
import { RANGE_TYPES, NUMBER_TYPES, isNumberInput } from "./advance-filter.helpers";

// Sized and styled like SearchableSelect so every editor in the drawer lines up.
const INPUT_CLASS =
  "w-full h-[38px] px-3 text-[13px] rounded border bg-surface text-text_primary placeholder:text-text_muted focus:outline-none focus:border-border_strong";

// Any / Yes / No as choice chips, shown on the field's own line.
export const BooleanChoices = ({ field, value, onChange }) => {
  const choices = [
    { value: "", label: "Any" },
    { value: "Yes", label: field.trueLabel || "Yes" },
    { value: "No", label: field.falseLabel || "No" },
  ];
  return (
    <div role="radiogroup" aria-label={field.label} className="flex flex-shrink-0 items-center gap-1.5">
      {choices.map((choice) => {
        const selected = (value || "") === choice.value;
        return (
          <button
            key={choice.label}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(choice.value || undefined)}
            className={`h-7 px-2.5 text-xs font-medium rounded-md border ${
              selected
                ? "bg-primary border-primary text-white"
                : "bg-surface border-border text-text_secondary hover:text-text_primary hover:border-border_strong"
            }`}
          >
            {choice.label}
          </button>
        );
      })}
    </div>
  );
};

// From → To on one line; with `stacked` (date + time, too wide for half a
// row) each bound gets its own line with a From / To label.
const RangeLayout = ({ stacked, from, to }) => {
  if (stacked) {
    return (
      <div className="space-y-2">
        {[
          ["From", from],
          ["To", to],
        ].map(([label, input]) => (
          <div key={label} className="grid grid-cols-[36px_minmax(0,1fr)] items-center gap-2">
            <span className="text-[11px] font-medium text-text_muted">{label}</span>
            {input}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1.5">
      <div className="min-w-0">{from}</div>
      <ArrowRight size={14} className="text-text_muted" aria-hidden="true" />
      <div className="min-w-0">{to}</div>
    </div>
  );
};

// With `showTime`, From starts at 00:00 and To at 23:59, so picking only days
// still covers them whole.
const DateRangeInput = ({ field, value = {}, onChange, error, timeFormat }) => {
  const picker = (bound, limits, defaultTime) => (
    <DateTimePicker
      value={value[bound] || null}
      onChange={(date) => onChange({ ...value, [bound]: date })}
      showTime={!!field.showTime}
      timeFormat={timeFormat}
      defaultTime={field.showTime ? defaultTime : null}
      placeholder={`${bound === "from" ? "Start" : "End"} ${field.showTime ? "date & time" : "date"}`}
      hasError={!!error}
      variant="select"
      {...limits}
    />
  );
  return (
    <RangeLayout
      stacked={!!field.showTime}
      from={picker("from", { maxDate: value.to }, { hour: 0, minute: 0 })}
      to={picker("to", { minDate: value.from }, { hour: 23, minute: 59 })}
    />
  );
};

const TimeRangeInput = ({ value = {}, onChange, error }) => {
  const input = (bound) => (
    <input
      type="time"
      aria-label={bound === "from" ? "From time" : "To time"}
      value={value[bound] || ""}
      onChange={(e) => onChange({ ...value, [bound]: e.target.value })}
      className={`${INPUT_CLASS} dark:[color-scheme:dark] ${error ? "border-danger" : "border-border"}`}
    />
  );
  return <RangeLayout from={input("from")} to={input("to")} />;
};

// Text box with a leading icon (search for text, # for numbers).
const IconInput = ({ icon: Icon, className = "", ...props }) => (
  <div className="relative">
    <Icon size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text_muted" />
    <input type="text" {...props} className={`${INPUT_CLASS} pl-9 border-border ${className}`} />
  </div>
);

const NumberInput = ({ field, value, onChange }) => (
  <IconInput
    icon={Hash}
    inputMode={field.type === "int" ? "numeric" : "decimal"}
    value={value ?? ""}
    onChange={(e) => {
      const text = e.target.value.trim();
      if (isNumberInput(field.type, text)) onChange(text || undefined);
    }}
    placeholder={field.placeholder || (field.type === "int" ? "Enter a whole number" : "Enter a number")}
    className="tabular-nums"
  />
);

const TextInput = ({ field, value, onChange }) => (
  <IconInput
    icon={Search}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value || undefined)}
    placeholder={field.placeholder || `Search ${field.label.toLowerCase()}`}
  />
);

// The editor for a field, chosen by its `type` (see advance-filter.helpers.js).
// Booleans use BooleanChoices instead.
const FilterFieldInput = ({ field, value, onChange, options, loading, error, timeFormat }) => {
  if (RANGE_TYPES.has(field.type)) {
    const RangeInput = field.type === "time" ? TimeRangeInput : DateRangeInput;
    return (
      <div className="space-y-1.5">
        <RangeInput field={field} value={value} onChange={onChange} error={error} timeFormat={timeFormat} />
        {error && <p className="text-[11px] text-danger">{error}</p>}
      </div>
    );
  }
  if (NUMBER_TYPES.has(field.type)) return <NumberInput field={field} value={value} onChange={onChange} />;
  if (field.type === "string") return <TextInput field={field} value={value} onChange={onChange} />;

  // select / enum
  const isLoading = loading && !field.options && !field.values;
  const placeholder = field.placeholder || `Select ${field.label.toLowerCase()}`;
  return field.single ? (
    <SearchableSelect
      options={options}
      value={value?.[0] ?? ""}
      onChange={(val) => onChange(val === "" ? undefined : [val])}
      placeholder={placeholder}
      isClearable
      isLoading={isLoading}
    />
  ) : (
    <SearchableSelect
      options={options}
      value={value || []}
      onChange={(vals) => onChange(vals.length ? vals : undefined)}
      placeholder={placeholder}
      isMulti
      checkboxes
      isClearable
      isLoading={isLoading}
    />
  );
};

export default FilterFieldInput;
